'use client';
import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { TrendingUp, Target, DollarSign, Calendar } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// Projection model:
// Base: $5.14M (FY2024 actual)
// Conservative: +35% registration/yr, 80% compliance
// Moderate: +55% registration/yr, 85% compliance (recommended)
// Aggressive: +75% registration/yr, 90% compliance
// Full potential: $24.4M at 100% registration

const SCENARIOS = {
  conservative: {
    label: 'Conservative',
    color: '#60a5fa',
    data: [
      { year: 'FY2024\n(Actual)', revenue: 5.14, registered: 74500, rate: 11 },
      { year: 'Year 1', revenue: 7.8, registered: 130000, rate: 20 },
      { year: 'Year 2', revenue: 11.2, registered: 195000, rate: 30 },
      { year: 'Year 3', revenue: 14.6, registered: 260000, rate: 40 },
      { year: 'Year 4', revenue: 17.4, registered: 325000, rate: 50 },
      { year: 'Year 5', revenue: 19.8, registered: 390000, rate: 60 },
    ],
  },
  moderate: {
    label: 'Moderate (Recommended)',
    color: '#d4a017',
    data: [
      { year: 'FY2024\n(Actual)', revenue: 5.14, registered: 74500, rate: 11 },
      { year: 'Year 1', revenue: 9.6, registered: 163000, rate: 25 },
      { year: 'Year 2', revenue: 14.2, registered: 261000, rate: 40 },
      { year: 'Year 3', revenue: 18.1, registered: 359000, rate: 55 },
      { year: 'Year 4', revenue: 21.3, registered: 457000, rate: 70 },
      { year: 'Year 5', revenue: 23.4, registered: 522000, rate: 80 },
    ],
  },
  aggressive: {
    label: 'Aggressive',
    color: '#4ade80',
    data: [
      { year: 'FY2024\n(Actual)', revenue: 5.14, registered: 74500, rate: 11 },
      { year: 'Year 1', revenue: 12.4, registered: 196000, rate: 30 },
      { year: 'Year 2', revenue: 17.8, registered: 327000, rate: 50 },
      { year: 'Year 3', revenue: 21.6, registered: 425000, rate: 65 },
      { year: 'Year 4', revenue: 23.8, registered: 490000, rate: 75 },
      { year: 'Year 5', revenue: 24.4, registered: 654000, rate: 100 },
    ],
  },
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-4 py-3 shadow-2xl text-xs"
      style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', minWidth: 180 }}>
      <p className="font-bold mb-2" style={{ color: 'var(--dark-text)' }}>{label?.replace('\n', ' ')}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4 mb-1">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-bold" style={{ color: p.color }}>${p.value.toFixed(2)}M</span>
        </div>
      ))}
    </div>
  );
}

type ScenarioKey = keyof typeof SCENARIOS;

export default function RevenueRecoveryTimeline() {
  const [activeScenario, setActiveScenario] = useState<ScenarioKey>('moderate');
  const scenario = SCENARIOS[activeScenario];
  const lastYear = scenario.data[scenario.data.length - 1];
  const totalGain = lastYear.revenue - 5.14;
  const multiplier = (lastYear.revenue / 5.14).toFixed(1);

  const milestones = [
    { year: 'Year 1', label: 'Break-Even on Platform Cost', color: '#60a5fa' },
    { year: 'Year 3', label: 'IMF Benchmark Threshold', color: '#fbbf24' },
    { year: 'Year 5', label: 'Regional Peer Parity', color: '#4ade80' },
  ];

  return (
    <section className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--dark-border)', background: 'var(--dark-card)' }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4" style={{
        borderBottom: '1px solid var(--dark-border)',
        background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, rgba(74,222,128,0.05) 100%)'
      }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,160,23,0.15)' }}>
            <TrendingUp size={16} style={{ color: '#d4a017' }} />
          </div>
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#d4a017' }}>Revenue Recovery Timeline</span>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>5-year projection under three deployment scenarios</p>
          </div>
        </div>
        <h2 className="text-lg font-bold mt-2" style={{ color: 'var(--dark-text)' }}>
          From $5.14M to $24.4M — The Path to Full Revenue Realisation
        </h2>
        <p className="text-xs mt-0.5 max-w-2xl" style={{ color: 'var(--dark-muted)' }}>
          Projections based on LRA pilot results (Margibi +428%, Grand Bassa +1,125%), USAID LEAD programme targets, and IMF Liberia Article IV consultation benchmarks.
        </p>
      </div>

      <div className="px-5 py-5">
        {/* Scenario selector */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <span className="text-xs font-semibold mr-1" style={{ color: 'var(--dark-muted)' }}>Scenario:</span>
          {(Object.keys(SCENARIOS) as ScenarioKey[]).map(key => (
            <button
              key={key}
              onClick={() => setActiveScenario(key)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: activeScenario === key ? `${SCENARIOS[key].color}22` : 'var(--dark-elevated)',
                color: activeScenario === key ? SCENARIOS[key].color : 'var(--dark-muted)',
                border: `1px solid ${activeScenario === key ? SCENARIOS[key].color + '44' : 'var(--dark-border)'}`,
              }}
            >
              {SCENARIOS[key].label}
            </button>
          ))}
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Year 5 Revenue', value: `$${lastYear.revenue.toFixed(1)}M`, color: scenario.color, icon: DollarSign },
            { label: 'Total 5-Yr Gain', value: `+$${totalGain.toFixed(1)}M`, color: '#4ade80', icon: TrendingUp },
            { label: 'Revenue Multiplier', value: `${multiplier}×`, color: '#fbbf24', icon: Target },
            { label: 'Year 5 Registration', value: `${lastYear.rate}%`, color: '#60a5fa', icon: Calendar },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="rounded-xl px-4 py-3" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon size={12} style={{ color }} />
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{label}</span>
              </div>
              <p className="text-xl font-black" style={{ color, letterSpacing: '-0.02em' }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="rounded-xl p-4" style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={scenario.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={scenario.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={scenario.color} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 10, fill: 'var(--dark-muted)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v.replace('\n', ' ')}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--dark-muted)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `$${v}M`}
                domain={[0, 26]}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={24.4} stroke="#4ade80" strokeDasharray="4 4" strokeWidth={1.5}
                label={{ value: 'Full Potential $24.4M', position: 'insideTopRight', fontSize: 10, fill: '#4ade80' }} />
              <ReferenceLine y={5.14} stroke="#f87171" strokeDasharray="4 4" strokeWidth={1}
                label={{ value: 'FY2024 Baseline $5.14M', position: 'insideBottomRight', fontSize: 10, fill: '#f87171' }} />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Projected Revenue (USD M)"
                stroke={scenario.color}
                strokeWidth={2.5}
                fill="url(#revenueGrad)"
                dot={{ fill: scenario.color, strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: scenario.color }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Milestones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {milestones.map(m => (
            <div key={m.year} className="rounded-xl px-4 py-3 flex items-center gap-3"
              style={{ background: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-xs font-bold"
                style={{ background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30` }}>
                {m.year}
              </div>
              <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
